/** Coordinates auth provider and token handling; implements the shell auth application port. */
import type { AuthProviderPort } from '../domain/ports/AuthProviderPort.ts';
import type { AuthTokenStoragePort } from '../domain/ports/AuthTokenStoragePort.ts';
import type {
  ShellAuthApplicationPort,
  ShellAuthUser,
} from '../../shared/app-context/domain/ports/ShellAuthApplicationPort.ts';
import { toShellAuthUser } from '../domain/utils/shellAuthUser.ts';

export class AuthService implements ShellAuthApplicationPort {
  private _isAuthenticated = false;
  private _currentUser: unknown = null;

  /** Constructs the authentication service. */
  constructor(
    private readonly authProvider: AuthProviderPort,
    private readonly tokenStorage: AuthTokenStoragePort | null = null,
  ) {}

  /** Initializes the user session. */
  async initializeSession(): Promise<void> {
    try {
      const session = await this.authProvider.getSession();
      this._isAuthenticated = !!session.accessToken;
      this._currentUser = session.user ?? null;

      if (this.tokenStorage && session.accessToken) {
        try {
          this.tokenStorage.setToken(session.accessToken);
        } catch {
          this._isAuthenticated = false;
          this._currentUser = null;
        }
      }
    } catch {
      this._isAuthenticated = false;
      this._currentUser = null;
    }
  }

  /** Logs in a user with email or username and password. */
  async login(usernameOrEmail: string, password: string): Promise<void> {
    const { accessToken, user } = await this.authProvider.login(usernameOrEmail, password);

    if (this.tokenStorage && accessToken) {
      try {
        this.tokenStorage.setToken(accessToken);
      } catch {
        this._isAuthenticated = false;
        this._currentUser = null;
        throw new Error('No se pudo guardar la sesión en el dispositivo.');
      }
    }

    this._isAuthenticated = true;
    this._currentUser = user ?? null;
  }

  /** Logs out the current user. */
  async logout(): Promise<void> {
    try {
      await this.authProvider.logout();
    } catch {
      /* Remote sign-out is best-effort (network / 401 handled in the adapter). */
    }

    if (this.tokenStorage) {
      try {
        this.tokenStorage.clearToken();
      } catch {
        /* Storage may fail; local state is still reset. */
      }
    }

    this._isAuthenticated = false;
    this._currentUser = null;
  }

  /** Checks if the user is authenticated. */
  async isAuthenticated(): Promise<boolean> {
    try {
      this._isAuthenticated = await this.authProvider.isAuthenticated();
      return this._isAuthenticated;
    } catch {
      return false;
    }
  }

  /** Retrieves the current user object. */
  async getCurrentUser(): Promise<ShellAuthUser | null> {
    if (!this._currentUser) {
      try {
        this._currentUser = await this.authProvider.getUser();
      } catch {
        this._currentUser = null;
      }
    }
    return toShellAuthUser(this._currentUser);
  }
}
